import {List} from 'immutable';
import {Moment} from 'moment';
import alt from 'components/Dispatcher';
import generateAsyncActions from 'utils/generateAsyncActions';
import DirectReportsSource from 'sources/coaching/DirectReportsSource';
import {ActionsBase, DispatchAll} from '@store/@types/alt';
import {UserId} from 'records/User';
import {UserSkillBreakdown} from 'records/UserSkillBreakdown';
import {PlanId} from 'records/CoachingPlan';
import Team from 'records/Team';
import {CoachingPlanVelocity} from 'records/CoachingPlanVelocity';
import {TeamId} from 'records/TeamId';
import {NoMethods} from '@store/@types/alt/Actions';

interface AsyncMethods {
  getSkillBreakdown: (userIds: UserId[]) => Promise<UserSkillBreakdown>;
  getActivityRankings: (teamIds: TeamId[], startDate: Moment, endDate: Moment) => Promise<any>;
  planVelocities: (planIds: List<PlanId>) => Promise<CoachingPlanVelocity>;
  directReports: (managerId: UserId) => Promise<List<Team>>;
}

/**
 * This slice of state represents actions and state that exist for
 *  1. a manager that has direct reports
 *  2. an exec that manages managers
 *
 * directReports: reps that report to that manager (regardless of role)
 * challenged by: metrics by userId to determine what skills the rep is challenged by
 * activity rankings: activity rankings in the system
 * plan velocities: manager level metrics by plan
 * time spent on coaching plans: by manager id, how much time does that manager spend on plans?
 * time spent on other coaching: ditto, but focused on other coaching throughout the app
 */
class DirectReportsActions
  extends ActionsBase<AsyncMethods, NoMethods>()
  implements DispatchAll<AsyncMethods>
{
  constructor() {
    super();
    this.generateActions(
      ...generateAsyncActions(
        'directReports',
        'getActivityRankings',
        'planVelocities',
        'getSkillBreakdown'
      )
    );
  }

  directReports(managerId) {
    return dispatch => {
      dispatch(managerId);
      return DirectReportsSource.directReports(managerId).then(
        this.directReportsSuccess,
        this.directReportsError
      );
    };
  }

  getActivityRankings(teamIds, startDate, endDate) {
    return dispatch => {
      dispatch(teamIds, startDate, endDate);
      return DirectReportsSource.getActivityRankings(teamIds, startDate, endDate).then(
        this.getActivityRankingsSuccess,
        this.getActivityRankingsError
      );
    };
  }

  getSkillBreakdown(userIds) {
    return dispatch => {
      dispatch(userIds);
      return DirectReportsSource.getSkillBreakdown(userIds).then(
        this.getSkillBreakdownSuccess,
        this.getSkillBreakdownError
      );
    };
  }

  planVelocities(planIds) {
    return dispatch => {
      dispatch(planIds);
      return DirectReportsSource.planVelocities(planIds).then(
        this.planVelocitiesSuccess,
        this.planVelocitiesError
      );
    };
  }
}

export default alt.createActions<AsyncMethods, NoMethods>(DirectReportsActions);
